import type { MaterialSlide } from './api';
import { slideBodyLines, slideVisualKind, slideVisualLabel } from './slide-visuals';

function ConstructionScene({ compact = false }: { compact?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={`construction-scene ${compact ? 'compact' : ''}`}
      viewBox="0 0 420 250"
    >
      <defs>
        <linearGradient id="sky" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#18395f" />
          <stop offset="1" stopColor="#0b1f38" />
        </linearGradient>
        <pattern height="18" id="hatch" patternUnits="userSpaceOnUse" width="18">
          <path d="M-4 4L4-4M0 18L18 0M14 22L22 14" stroke="#f7c94b" strokeWidth="5" />
        </pattern>
      </defs>
      <rect fill="url(#sky)" height="250" rx="18" width="420" />
      <circle cx="342" cy="56" fill="#f7c94b" opacity=".9" r="27" />
      <path d="M0 175L95 129l63 31 91-53 171 68v75H0z" fill="#132f50" />
      <path d="M0 205h420v45H0z" fill="url(#hatch)" opacity=".92" />
      <g transform="translate(108 105)">
        <path d="M35 44h119l25 39H12z" fill="#f3b91f" />
        <path d="M53 7h76l22 37H36z" fill="#ffd76c" />
        <path d="M115 14h15l15 27h-30z" fill="#234669" />
        <rect fill="#0c2039" height="14" rx="7" width="185" x="3" y="78" />
        <circle cx="39" cy="91" fill="#071523" r="22" />
        <circle cx="39" cy="91" fill="#a7b4c3" r="8" />
        <circle cx="151" cy="91" fill="#071523" r="22" />
        <circle cx="151" cy="91" fill="#a7b4c3" r="8" />
      </g>
      <g fill="none" stroke="#f7c94b" strokeLinecap="round" strokeWidth="5">
        <path d="M87 96c-27 15-39 38-39 67" />
        <path d="M48 163l-10-16m10 16 15-11" />
      </g>
    </svg>
  );
}

function RiskDiagram() {
  return (
    <div className="risk-diagram" aria-label="車両周辺の危険範囲を示す概念図">
      <div className="risk-orbit risk-orbit-one" />
      <div className="risk-orbit risk-orbit-two" />
      <svg aria-hidden="true" className="truck-top" viewBox="0 0 170 110">
        <path d="M18 25h92l35 22v38H18z" fill="#f3b91f" stroke="#10233f" strokeWidth="5" />
        <path d="M109 30v51" stroke="#10233f" strokeWidth="5" />
        <rect fill="#234669" height="26" rx="3" width="27" x="116" y="45" />
        <path d="M5 36h18m-18 38h18m118-38h18m-18 38h18" stroke="#10233f" strokeWidth="10" />
      </svg>
      <div className="reverse-arrow">後退方向</div>
      <div className="risk-tag">危険範囲・死角を現場で確認</div>
      <small>概念図</small>
    </div>
  );
}

function ChecklistGraphic({ lines }: { lines: string[] }) {
  const items = lines.length > 0 ? lines.slice(0, 4) : ['当日の現場条件を確認'];
  return (
    <div className="visual-checklist">
      {items.map((line, index) => (
        <div className="visual-check-item" key={`${line}-${index}`}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <strong>{line}</strong>
          <i aria-hidden="true">✓</i>
        </div>
      ))}
    </div>
  );
}

function ProcedureGraphic({ lines }: { lines: string[] }) {
  const items = lines.length > 0 ? lines.slice(0, 3) : ['承認済み手順を確認'];
  return (
    <div className="procedure-flow">
      {items.map((line, index) => (
        <div className="procedure-step" key={`${line}-${index}`}>
          <span>{index + 1}</span>
          <strong>{line}</strong>
        </div>
      ))}
    </div>
  );
}

function QuizGraphic({ lines }: { lines: string[] }) {
  const [question = '', ...choices] = lines;
  return (
    <div className="quiz-visual">
      <div className="quiz-question-mark">?</div>
      <div>
        <strong>{question}</strong>
        <div className="quiz-options">
          {choices.slice(0, 4).map((choice, index) => (
            <span key={`${choice}-${index}`}>{choice}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function AgendaGraphic() {
  return (
    <div className="agenda-flow">
      {['現場条件', '安全ポイント', '理解確認'].map((label, index) => (
        <div className="agenda-step" key={label}>
          <span>{index + 1}</span>
          <strong>{label}</strong>
        </div>
      ))}
    </div>
  );
}

function ApprovedSourceGraphic() {
  return (
    <div className="source-graphic">
      <div className="source-page">
        <span />
        <span />
        <span />
        <i>✓</i>
      </div>
      <div>
        <strong>承認済み情報</strong>
        <small>登録済みの出典範囲で説明</small>
      </div>
    </div>
  );
}

function MediaGraphic({
  alt,
  mediaType,
  mediaUrl,
}: {
  alt: string;
  mediaType: string | null | undefined;
  mediaUrl: string;
}) {
  if (mediaUrl === undefined) return null;
  return mediaType === 'VIDEO' ? (
    <video controls preload="metadata" src={mediaUrl} />
  ) : (
    <img alt={alt} loading="lazy" src={mediaUrl} />
  );
}

export function SlideVisual({ mediaUrl, slide }: { mediaUrl?: string; slide: MaterialSlide }) {
  const kind = slideVisualKind(slide.type, slide.title);
  const lines = slideBodyLines(slide.body);
  const diagramUrl = slide.composition?.diagramAssetPath ?? undefined;
  const visualUrl = mediaUrl ?? diagramUrl;
  const visualKind = slide.composition?.visualKind;
  const hasMedia = visualUrl !== undefined;

  let graphic: React.ReactNode;
  if (hasMedia) {
    graphic = (
      <MediaGraphic
        alt={slide.composition?.visualAltText ?? `「${slide.title}」の確認画像`}
        mediaType={mediaUrl === undefined ? 'ILLUSTRATION' : slide.mediaAssetType}
        mediaUrl={visualUrl}
      />
    );
  } else if (visualKind === 'FLOW') {
    graphic = kind === 'agenda' ? <AgendaGraphic /> : <ProcedureGraphic lines={lines} />;
  } else if (visualKind === 'QUIZ') {
    graphic = <QuizGraphic lines={lines} />;
  } else if (visualKind === 'SOURCE') {
    graphic = <ApprovedSourceGraphic />;
  } else if (visualKind === 'CHECKLIST') {
    graphic = <ChecklistGraphic lines={lines} />;
  } else if (visualKind === 'RISK_MAP' || visualKind === 'COMPARISON') {
    graphic = <RiskDiagram />;
  } else if (kind === 'cover') {
    graphic = <ConstructionScene />;
  } else if (kind === 'hazard' || kind === 'ky') {
    graphic = <RiskDiagram />;
  } else if (kind === 'agenda') {
    graphic = <AgendaGraphic />;
  } else if (kind === 'procedure') {
    graphic = <ProcedureGraphic lines={lines} />;
  } else if (kind === 'quiz') {
    graphic = <QuizGraphic lines={lines} />;
  } else if (kind === 'reference') {
    graphic = <ApprovedSourceGraphic />;
  } else if (kind === 'summary' || kind === 'closing') {
    graphic = <ChecklistGraphic lines={lines} />;
  } else {
    graphic = <ConstructionScene compact />;
  }

  return (
    <div
      className={`slide-visual-layout visual-${kind} layout-${(slide.composition?.layoutFamily ?? 'LEGACY').toLocaleLowerCase('en-US').replaceAll('_', '-')}`}
    >
      <div className="slide-copy">
        {visualKind === 'QUIZ' || kind === 'quiz' ? (
          <span className="slide-copy-lead">参加者が回答してから解説を確認</span>
        ) : (
          lines.map((line, index) => (
            <p key={`${slide.id}-line-${index}`}>
              <span aria-hidden="true" />
              {line}
            </p>
          ))
        )}
      </div>
      <div className={`slide-graphic-panel ${hasMedia ? 'has-media' : ''}`}>
        <span className="graphic-kicker">{slideVisualLabel(kind)}</span>
        {graphic}
      </div>
    </div>
  );
}
